// Error Helpers - Pure helper functions for error classification and normalization

import { ErrorEvents } from "../constants/Events";
import type { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  DEFAULT_NOTIFICATION_DURATION,
  NOTIFICATION_DURATIONS,
  ErrorSeverity,
  ErrorSource,
  USER_FRIENDLY_MESSAGES,
} from "./error-constants";
import type {
  AppError,
  ErrorContext,
  ErrorDismissedPayload,
  ErrorHandlerOptions,
  ErrorNotification,
  ErrorRetryRequestedPayload,
} from "./error-types";
import { getErrorMessage, getErrorCode } from "./types/ErrorStructure";

/**
 * Generate a unique error ID
 */
export function generateErrorId(): string {
  return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Classify an Error instance by its `name` property into a source and severity.
 * Returns undefined for errors that cannot be classified by name.
 */
export function classifyErrorByName(
  errorName: string,
): { source: ErrorSource; severity: ErrorSeverity } | undefined {
  switch (errorName) {
    case "ApiError":
      return { source: ErrorSource.API, severity: ErrorSeverity.ERROR };
    case "ValidationError":
      return {
        source: ErrorSource.VALIDATION,
        severity: ErrorSeverity.WARNING,
      };
    case "AuthError":
      return { source: ErrorSource.AUTH, severity: ErrorSeverity.ERROR };
    case "NetworkError":
      return {
        source: ErrorSource.NETWORK,
        severity: ErrorSeverity.WARNING,
      };
    default:
      return undefined;
  }
}

/**
 * Generate a user-friendly error message based on the error source
 */
export function generateUserFriendlyMessage(
  message: string,
  source: ErrorSource,
): string {
  return (
    USER_FRIENDLY_MESSAGES[source] ?? "Something went wrong. Please try again."
  );
}

/**
 * Map error severity to notification severity
 */
export function mapSeverityToNotificationSeverity(
  severity: ErrorSeverity,
): "error" | "warning" | "info" {
  switch (severity) {
    case ErrorSeverity.CRITICAL:
    case ErrorSeverity.ERROR:
      return "error";
    case ErrorSeverity.WARNING:
      return "warning";
    case ErrorSeverity.INFO:
      return "info";
    default:
      return "error";
  }
}

/**
 * Get notification duration based on severity
 */
export function getNotificationDuration(severity: ErrorSeverity): number {
  return NOTIFICATION_DURATIONS[severity] ?? DEFAULT_NOTIFICATION_DURATION;
}

/**
 * Options for normalizing an unknown error into an AppError
 */
export interface NormalizeErrorOptions {
  retryable?: boolean;
  userFriendlyMessage?: string;
  severity?: ErrorSeverity;
  source?: ErrorSource;
  context?: Partial<ErrorContext>;
}

/**
 * Normalize any error value to a standardized AppError
 */
export function normalizeError(
  error: unknown,
  options: NormalizeErrorOptions = {},
): AppError {
  const {
    retryable = false,
    userFriendlyMessage,
    severity,
    source: sourceOverride,
    context = {},
  } = options;

  // Extract error information
  let message = "An unknown error occurred";
  let source = ErrorSource.UNKNOWN;
  let finalSeverity = ErrorSeverity.ERROR; // Default to ERROR
  let code: string | undefined;
  let stack: string | undefined;

  if (error instanceof Error) {
    message = error.message;
    stack = error.stack;

    // Determine source and severity based on error type
    const classification = classifyErrorByName(error.name);
    if (classification) {
      source = classification.source;
      finalSeverity = classification.severity;
    }
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object" && "message" in error) {
    message = getErrorMessage(error);
    code = getErrorCode(error) as string | undefined;
  }

  // Override source if provided in options
  if (sourceOverride) {
    source = sourceOverride;
  }

  // Override severity if provided in options
  if (severity) {
    finalSeverity = severity;
  }

  // Create context
  const errorContext: ErrorContext = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...context,
  };

  // Generate user-friendly message
  const friendlyMessage =
    userFriendlyMessage || generateUserFriendlyMessage(message, source);

  return {
    id: generateErrorId(),
    message,
    severity: finalSeverity,
    source,
    context: errorContext,
    originalError: error,
    stack,
    code,
    retryable,
    userFriendlyMessage: friendlyMessage,
  };
}

/**
 * Create an empty errors-by-severity record initialized to zero counts
 */
export function createEmptyErrorsBySeverity(): Record<ErrorSeverity, number> {
  return {
    [ErrorSeverity.INFO]: 0,
    [ErrorSeverity.WARNING]: 0,
    [ErrorSeverity.ERROR]: 0,
    [ErrorSeverity.CRITICAL]: 0,
  };
}

/**
 * Create an empty errors-by-source record initialized to zero counts
 */
export function createEmptyErrorsBySource(): Record<ErrorSource, number> {
  return {
    [ErrorSource.API]: 0,
    [ErrorSource.AUTH]: 0,
    [ErrorSource.VALIDATION]: 0,
    [ErrorSource.NETWORK]: 0,
    [ErrorSource.APPLICATION]: 0,
    [ErrorSource.INTEGRATION]: 0,
    [ErrorSource.UI]: 0,
    [ErrorSource.UNKNOWN]: 0,
  };
}

/**
 * Log an AppError with the appropriate debug level based on its severity
 */
export function logAppError(error: AppError): void {
  const logData = {
    id: error.id,
    message: error.message,
    severity: error.severity,
    source: error.source,
    context: error.context,
    stack: error.stack,
  };

  switch (error.severity) {
    case ErrorSeverity.CRITICAL:
      debugLogger.error("error-handler", "CRITICAL ERROR", logData);
      break;
    case ErrorSeverity.ERROR:
      debugLogger.error("error-handler", "ERROR", logData);
      break;
    case ErrorSeverity.WARNING:
      debugLogger.warn("error-handler", "WARNING", logData);
      break;
    case ErrorSeverity.INFO:
      debugLogger.info("error-handler", "INFO", logData);
      break;
  }
}

/**
 * Build an ErrorNotification from an AppError.
 * If `retryHandler` is provided and the error is retryable, a Retry action is attached.
 */
export function buildErrorNotification(
  error: AppError,
  retryHandler?: () => void,
): ErrorNotification {
  return {
    message: error.userFriendlyMessage,
    severity: mapSeverityToNotificationSeverity(error.severity),
    duration: getNotificationDuration(error.severity),
    action:
      error.retryable && retryHandler
        ? {
            label: "Retry",
            onClick: retryHandler,
          }
        : undefined,
  };
}

/**
 * Build ErrorHandlerOptions for an API error
 */
export function buildApiErrorOptions(
  endpoint?: string,
  options: ErrorHandlerOptions = {},
): ErrorHandlerOptions {
  return {
    ...options,
    context: {
      ...options.context,
      action: "api-request",
      metadata: {
        ...options.context?.metadata,
        endpoint,
      },
    },
  };
}

/**
 * Build ErrorHandlerOptions for a validation error
 */
export function buildValidationErrorOptions(
  field?: string,
  options: ErrorHandlerOptions = {},
): ErrorHandlerOptions {
  return {
    ...options,
    severity: ErrorSeverity.WARNING,
    context: {
      ...options.context,
      action: "validation",
      metadata: {
        ...options.context?.metadata,
        field,
      },
    },
  };
}

/**
 * Build ErrorHandlerOptions for an auth error
 */
export function buildAuthErrorOptions(
  action?: string,
  options: ErrorHandlerOptions = {},
): ErrorHandlerOptions {
  return {
    ...options,
    context: {
      ...options.context,
      action: action || "authentication",
    },
  };
}

/**
 * Build ErrorHandlerOptions for a manually-created error
 */
export function buildCreateErrorOptions(
  severity: ErrorSeverity,
  options: ErrorHandlerOptions = {},
): ErrorHandlerOptions {
  return {
    ...options,
    severity,
    context: {
      ...options.context,
      action: "manual-error-creation",
    },
  };
}

/**
 * Emit an error notification show event
 */
export function emitErrorNotification(
  eventBus: EventBus,
  notification: ErrorNotification,
): void {
  eventBus.emitGlobal(ErrorEvents.ERROR_NOTIFICATION_SHOW, notification);
}

/**
 * Report an error to analytics via the event bus
 */
export function reportErrorToAnalytics(
  eventBus: EventBus,
  error: AppError,
): void {
  eventBus.emitGlobal(ErrorEvents.ERROR_ANALYTICS_REPORT, {
    errorId: error.id,
    severity: error.severity,
    source: error.source,
    message: error.message,
    context: error.context,
    timestamp: error.context.timestamp,
  });
}

/**
 * Emit an error-occurred event for other systems
 */
export function emitErrorOccurredEvent(
  eventBus: EventBus,
  error: AppError,
): void {
  eventBus.emitGlobal(ErrorEvents.ERROR_OCCURRED, {
    error,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Emit an error-retry-requested event
 */
export function emitErrorRetryRequested(
  eventBus: EventBus,
  error: AppError,
): void {
  eventBus.emitGlobal(ErrorEvents.ERROR_RETRY_REQUESTED, {
    errorId: error.id,
    context: error.context,
  });
}

/**
 * Setup global error event handlers (retry requests + dismissals)
 */
export function setupErrorEventHandlers(eventBus: EventBus): void {
  eventBus.subscribeGlobal(
    ErrorEvents.ERROR_RETRY_REQUESTED,
    (payload: ErrorRetryRequestedPayload) => {
      debugLogger.info("error-handler", "Error retry requested", payload);
    },
  );

  eventBus.subscribeGlobal(
    ErrorEvents.ERROR_DISMISSED,
    (payload: ErrorDismissedPayload) => {
      debugLogger.info("error-handler", "Error dismissed", payload);
    },
  );
}
